/**
 * The screen for a signed-in person who belongs to no tenant yet.
 *
 * It is a full screen rather than an empty state inside the app shell, because
 * without a tenant there is no app: every query under `tenants/{id}` has nothing
 * to name. The two ways out are both here — accept an invitation someone sent
 * you, or start a kennel of your own — and invitations come first, since a
 * person who was invited and instead creates a second empty kennel has produced
 * exactly the mess that is hardest to undo later.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import { useInvitesForEmail } from '@/api/invites.ts';
import { createTenant } from '@/api/mutations.ts';
import { CollarStrip, Wordmark } from '@/components/Brand.tsx';
import { DisplayNameField } from '@/components/DisplayNameField.tsx';
import { PendingInvites } from '@/components/PendingInvites.tsx';
import { Button, Panel, TextField } from '@/components/ui.tsx';

export const Onboarding = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  // Google always supplies one; the empty string is the impossible case, and it
  // costs a query that matches nothing rather than a branch around a hook.
  const email = user?.email ?? '';
  const { data } = useInvitesForEmail(email);
  const invites = data ?? [];

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-8 px-6 py-10">
      <header>
        <CollarStrip className="mb-6" />
        <h1 className="text-2xl leading-none">
          <Wordmark />
        </h1>
      </header>

      {/*
        The one thing this app asks a new person for, and it does not insist:
        whichever way out of this screen they take, their name is about to be
        read by somebody else — beside a weight, in the people list — and this is
        the only moment where asking for it is not an interruption of something
        else. Above the invitation rather than below it because it is a field and
        not an action: it does not compete with a red button, and a person who
        ignores it loses nothing.
      */}
      <DisplayNameField />

      <PendingInvites invites={invites} />

      {/*
        When an invitation is waiting, joining it is the action and starting a
        second kennel is the mistake — someone who was invited and instead
        creates their own ends up with an empty tenant and their litter in
        somebody else's. Two identical red buttons would put those a
        thumb's-width apart and weight them the same.
      */}
      <CreateTenant uid={user?.uid ?? ''} secondary={invites.length > 0} />

      <div className="mt-auto pt-4 text-center text-sm text-ink-faint">
        <p>{t('auth.signedInAs', { email })}</p>
        <Button variant="quiet" className="-mx-2 mt-1 px-2 text-sm" onClick={() => void signOut()}>
          {t('auth.signOut')}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

/** How long to wait before saying out loud that this one needs a network. */
const SLOW_MS = 8000;

const CreateTenant = ({ uid, secondary }: { uid: string; secondary: boolean }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [breed, setBreed] = useState('');
  const [creating, setCreating] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (name.trim() === '') {
      setError(t('start.needsName'));
      return;
    }

    setError(null);
    setCreating(true);
    setSlow(false);

    /**
     * The one write in this app that is allowed to keep the user waiting, and
     * the reason is in api/mutations.ts: a tenant is unusable until the
     * onCreate function has written the owner membership, and a function cannot
     * run on a phone with no route out.
     *
     * The wait is announced on a TIMER, never on `navigator.onLine`. That flag
     * reports true for a phone attached to an access point with no route
     * anywhere — precisely the outbuilding this app was built for — so a form
     * that refused to submit on its say-so would be a form that refuses to work
     * where it is needed. Eight seconds of silence is the honest signal.
     */
    const timer = setTimeout(() => setSlow(true), SLOW_MS);

    createTenant({ name: name.trim(), breed: breed.trim(), country: null, createdBy: uid })
      .catch(() => {
        setError(t('start.failed'));
        setCreating(false);
      })
      .finally(() => clearTimeout(timer));
    // No success branch: the membership function's write reaches
    // TenantProvider through its listener and this whole screen goes away.
  };

  return (
    <Panel className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-bold tracking-[-0.01em]">{t('start.title')}</h2>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">{t('start.lead')}</p>
      </div>

      <TextField
        label={t('start.nameLabel')}
        placeholder={t('start.namePlaceholder')}
        value={name}
        autoComplete="organization"
        onChange={(event) => setName(event.target.value)}
        error={error ?? undefined}
      />

      <TextField
        label={`${t('start.breedLabel')} · ${t('common.optional')}`}
        placeholder={t('start.breedPlaceholder')}
        value={breed}
        onChange={(event) => setBreed(event.target.value)}
      />

      <Button full variant={secondary ? 'secondary' : 'primary'} onClick={submit} disabled={creating}>
        {creating ? t('start.creating') : t('start.submit')}
      </Button>

      {slow && <p className="text-sm text-ink-soft">{t('start.takingLonger')}</p>}
    </Panel>
  );
};
